import { type PowerInfo, getPowerCategoryLabel } from '@/lib/powers-data';
import { PowerSectionSmileyIcon } from '@/components/help-center/powers/power-gallery-view';
import {
  PowerDescriptionContent,
  RICH_HTML_LIST_CLASS,
} from '@/components/help-center/powers/PowerMentionText';
import { getCalloutColor, type CalloutColorId } from '@/lib/callout-colors';
import { cn } from '@/lib/utils';

interface PowerDetailOverviewProps {
  power: PowerInfo;
}

export default function PowerDetailOverview({ power }: PowerDetailOverviewProps) {
  const isEverypower = power.slug === 'everypower';
  const isAllpowers = power.slug === 'allpowers';

  return (
    <div className="bg-white">
      <section className="border-b border-gray-100 py-14">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-8 flex items-center gap-3">
            <PowerSectionSmileyIcon code={power.overviewSmiley?.trim() || 'read'} size="md" />
            <div>
              <p className="font-poppins text-xs font-semibold uppercase tracking-widest text-blue-500">
                {power.overviewSubtitle?.trim() || 'Documentação'}
              </p>
              <h2 className="font-poppins text-xl font-bold text-gray-900">
                {power.overviewTitle?.trim() || 'Sobre este power'}
              </h2>
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <p className="font-inter text-base leading-relaxed text-gray-600">
              <PowerDescriptionContent
                text={power.description.long || power.description.short}
                as="span"
              />
            </p>

            <div className="flex flex-col gap-4">
              {isEverypower ? (
                <>
                  <Callout
                    color="info"
                    title="Como obter"
                    content="É necessário possuir pelo menos um exemplar de cada power já lançado no xat. Sempre que um novo power é lançado (geralmente semanalmente), você precisa adquiri-lo para recuperar o EveryPower e o peão Esmeralda."
                  />
                  <Callout
                    color="warning"
                    title="Funcionalidade, não power negociável"
                    content="EveryPower não é um power que pode ser comprado ou trocado no Trade. Cada power da sua conta deve ser seleccionado individualmente ao negociar. Desconfie de ofertas que vendam EveryPower como item único."
                  />
                </>
              ) : isAllpowers ? (
                <>
                  <Callout
                    color="info"
                    title="Como obter"
                    content="AllPowers activa-se quando você possui pelo menos um exemplar de cada power que faz parte do conjunto. Use «Mark All Powers» na loja xat ou consulte a wiki para ver quais ainda faltam. Novos powers podem ser adicionados ao conjunto a qualquer momento."
                  />
                  <Callout
                    color="warning"
                    title="Conquista, não power à venda"
                    content="AllPowers não é um power que se compra ou troca no Trade. Desbloqueia o chapéu Golden Egg, efeitos #allpowers nos smilies e o smilie (allpowers) — mas exige completar o conjunto de powers exigidos."
                  />
                </>
              ) : (
                <>
                  <Callout
                    color={power.calloutHowItWorksColor ?? 'info'}
                    title={power.calloutHowItWorksTitle?.trim() || 'Como funciona'}
                    content={
                      power.calloutHowItWorks?.trim() ||
                      `O power ${power.name} é da categoria ${getPowerCategoryLabel(power.category)}. ${
                        power.limited
                          ? 'Por ser um power limitado, seu estoque é restrito — após esgotar, só pode ser obtido via fairtrade entre usuários.'
                          : 'Por ser ilimitado, você pode adquiri-lo na loja do xat a qualquer momento.'
                      }`
                    }
                  />

                  {power.limited ? (
                    <Callout
                      color={power.calloutLimitedColor ?? 'warning'}
                      title={power.calloutLimitedTitle?.trim() || 'Power Limitado'}
                      content={
                        power.calloutLimited?.trim() ||
                        `${power.name} é uma edição especial e limitada. Powers limitados costumam ter valor mais elevado no mercado fairtrade por serem mais raros e cobiçados pelos colecionadores.`
                      }
                    />
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Callout({
  color = 'info',
  title,
  content,
}: {
  color?: CalloutColorId | string;
  title: string;
  content: string;
}) {
  const s = getCalloutColor(color, 'info');
  const Icon = s.iconComp;
  return (
    <div className={cn('rounded-xl border p-4', s.bg, s.border)}>
      <div className="flex items-start gap-3">
        <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', s.icon)} />
        <div className="min-w-0 flex-1">
          <p className={cn('mb-1 font-poppins text-sm font-semibold', s.text)}>{title}</p>
          <div className={cn('font-inter text-sm leading-relaxed opacity-90', s.text, RICH_HTML_LIST_CLASS)}>
            <PowerDescriptionContent text={content} as="span" />
          </div>
        </div>
      </div>
    </div>
  );
}
